import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Target, Sparkles, Users, FileText, ArrowRight, X } from "lucide-react";

const SLIDES = [
  {
    icon: Target,
    iconBg: "bg-evergreen-950",
    title: "Welcome to Varyynt Intel",
    subtitle: "Talent intelligence that goes beyond the resume",
    body: "You now have access to a curated pool of passive pharmaceutical and biotech professionals — people who aren't actively applying but have told us exactly what their ideal next move looks like.",
    accent: "This is intent data. No recruiter, no LinkedIn Recruiter license, and no job board can give you this.",
  },
  {
    icon: Sparkles,
    iconBg: "bg-teal-700",
    title: "Intent Data: Your Unfair Advantage",
    subtitle: "What candidates want — not just what they've done",
    body: "Traditional sourcing tells you where someone has been. Varyynt tells you where they want to go — the company size, therapeutic focus, role scope, and work environment that would make them say yes.",
    accent: "Skip the months of recruiter outreach. Surface candidates whose career trajectory already points toward your role.",
  },
  {
    icon: Users,
    iconBg: "bg-lime-700",
    title: "Deep Analysis: AI-Powered Scoring",
    subtitle: "Every candidate ranked by fit and intent alignment",
    body: "Define your role criteria, then run Deep Analysis. Our AI scores each candidate on two dimensions: how well their experience matches your requirements, and how closely their career intent aligns with what your role offers.",
    accent: "The result: a ranked shortlist where the top candidates aren't just qualified — they're likely to be interested.",
  },
  {
    icon: FileText,
    iconBg: "bg-evergreen-900",
    title: "Work Orders: From Insight to Action",
    subtitle: "Move from intelligence to engagement in three clicks",
    body: "Select your top-scored candidates, create a Work Order, and the Varyynt team handles de-identified outreach on your behalf. Candidates respond without knowing which company is reaching out — protecting both sides until there's mutual interest.",
    accent: "No retained search fees. No contingency risk. Just precision engagement powered by intent.",
  },
];

export default function WelcomeOnboarding({ onComplete }) {
  const { api, user } = useAuth();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (user && !user.onboarding_completed) {
      setVisible(true);
    }
  }, [user]);

  const handleComplete = async () => {
    setVisible(false);
    try {
      await api.put("/settings/onboarding", { onboarding_completed: true });
    } catch {}
    onComplete?.();
  };

  const handleSkip = async () => {
    setVisible(false);
    try {
      await api.put("/settings/onboarding", { onboarding_completed: true });
    } catch {}
  };

  if (!visible) return null;

  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="welcome-onboarding">
      <div className="absolute inset-0 bg-evergreen-950/60 backdrop-blur-sm" onClick={handleSkip} />
      <div className="relative w-full max-w-lg mx-4 animate-slideUp">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header accent bar */}
          <div className="h-1.5 bg-gradient-to-r from-evergreen-950 via-lime-600 to-teal-500" />

          <div className="p-8">
            {/* Skip */}
            <button onClick={handleSkip} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors" data-testid="onboarding-skip">
              <X className="w-5 h-5" />
            </button>

            {/* Icon */}
            <div className={`w-14 h-14 rounded-xl ${slide.iconBg} flex items-center justify-center mb-6`}>
              <slide.icon className="w-7 h-7 text-white" />
            </div>

            {/* Content */}
            <h2 className="text-xl font-heading text-slate-900 mb-1">{slide.title}</h2>
            <p className="text-sm font-accent text-teal-700 mb-4">{slide.subtitle}</p>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">{slide.body}</p>
            <div className="bg-lime-50 border border-lime-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-lime-800 font-medium leading-relaxed">{slide.accent}</p>
            </div>

            {/* Progress + Actions */}
            <div className="flex items-center justify-between">
              <div className="flex gap-1.5">
                {SLIDES.map((_, i) => (
                  <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-8 bg-evergreen-950" : i < step ? "w-4 bg-lime-400" : "w-4 bg-slate-200"}`} />
                ))}
              </div>
              <div className="flex gap-2">
                {step > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} className="text-slate-500">
                    Back
                  </Button>
                )}
                {isLast ? (
                  <Button onClick={handleComplete} className="bg-evergreen-950 hover:bg-evergreen-900 text-white font-accent" data-testid="onboarding-finish">
                    Get Started <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button onClick={() => setStep(s => s + 1)} className="bg-evergreen-950 hover:bg-evergreen-900 text-white font-accent" data-testid="onboarding-next">
                    Next <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
